import { getValue } from 'containers/DownloadReport/helpers';
import { all, call, put } from 'redux-saga/effects';
import { requestForSaga } from 'utils/request/request';
import { requestCustomErrorMessage } from 'utils/request/requestCustomErrorMessage';
import { mapTables } from './helpers';

import {
  FactoryPreset,
  date,
  factoryPresetEdges,
  getEdgeSettingsById,
} from '@ipfabric/utils';
import { createGraphApiRequest } from 'components/Graph/helpers/createGraphApiRequest';
import { takeEvery } from 'containers/ErrorBoundary';
import { produce } from 'immer';
import get from 'lodash/get';
import { FETCH_SITE_REPORT_DATA } from './constants';

function* fetchDownloadSiteReportSaga(action) {
  const {
    attributeFilters,
    dateFormat,
    dateTimeFormat,
    reject,
    resolve,
    siteName,
    snapshot,
    timezone,
  } = action.payload;
  const siteFilter = { siteName: ['eq', siteName] };
  try {
    const [
      analysis,
      cdpAll,
      cdpUnidirectional,
      cdpUnmanaged,
      configRegister,
      connectivityMatrix,
      devices,
      endOfSupport,
      ethChannels,
      graph,
      hsrpStatus,
      intErrDisabled,
      managedIp,
      partNumbers,
      siteInfo,
      telnetDevices,
      unexpectedReloads,
      osVersionsPlatforms,
      duplexInterfaces,
      vlans,
    ] = yield all([
      call(requestForSaga, {
        url: '/tables/management/discovery-runs',
        method: 'POST',
        data: {
          columns: ['tsEnd'],
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/neighbors/all',
        method: 'POST',
        data: {
          columns: [
            'localHost',
            'localInt',
            'protocols',
            'remoteHost',
            'remoteInt',
          ],
          filters: siteFilter,
          reports: '/technology/cdp-lldp/all-neighbors',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/neighbors/unidirectional',
        method: 'POST',
        data: {
          columns: [
            'localHost',
            'localInt',
            'protocols',
            'remoteHost',
            'remoteInt',
            'remoteIp',
          ],
          filters: siteFilter,
          reports: '/technology/cdp-lldp/unidirectional-neighbors',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/neighbors/unmanaged',
        method: 'POST',
        data: {
          columns: [
            'localHost',
            'localInt',
            'protocols',
            'remoteHost',
            'remoteInt',
            'remoteIp',
          ],
          filters: siteFilter,
          reports: '/technology/cdp-lldp/unmanaged-neighbors',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/inventory/devices',
        method: 'POST',
        data: {
          columns: [
            'hostname',
            'uptime',
            'loginIpv4',
            'loginIpv6',
            'configReg',
          ],
          filters: {
            or: [
              { configReg: ['color', 'eq', '20'] },
              { configReg: ['color', 'eq', '30'] },
            ],
            ...siteFilter,
          },
          snapshot,
          reports: '/tables/inventory/devices',
        },
      }),
      call(requestForSaga, {
        url: '/tables/interfaces/connectivity-matrix',
        method: 'POST',
        data: {
          columns: [
            'localHost',
            'localSn',
            'localInt',
            'localMedia',
            'remoteHost',
            'remoteSn',
            'remoteInt',
            'remoteMedia',
            'protocol',
          ],
          filters: siteFilter,
          pagination: { limit: 100, start: 0 },
          snapshot,
        },
        reports:
          '/technology/interfaces/connectivity-matrix/connectivity-matrix',
      }),
      call(requestForSaga, {
        url: '/tables/inventory/devices',
        method: 'POST',
        data: {
          columns: [
            'configReg',
            'hostname',
            'image',
            'loginIpv4',
            'loginIpv6',
            'processor',
            'reload',
            'sn',
            'uptime',
            'version',
            'model',
          ],
          filters: siteFilter,
          reports: '/inventory/devices',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/reports/eof/summary',
        method: 'POST',
        data: {
          bindVariables: { siteName },
          columns: [
            'pid',
            'pidCount',
            'endSale',
            'endMaintenance',
            'endSupport',
          ],
          filters: {
            or: [
              { endSupport: ['color', 'eq', '20'] },
              { endSupport: ['color', 'eq', '30'] },
            ],
          },
          reports: '/inventory/end-of-life-milestones/summary',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/interfaces/port-channel/member-status',
        method: 'POST',
        data: {
          columns: ['hostname', 'intName', 'protocol', 'members'],
          reports: '/technology/port-channels/member-status-table',
          filters: siteFilter,
          snapshot,
        },
      }),
      call(
        requestForSaga,
        createGraphApiRequest({
          attributeFilters,
          parameters: {
            groupBy: 'siteName',
            paths: [siteName],
            type: 'topology',
          },
          snapshotId: snapshot,
          preset: produce(FactoryPreset, (draft) => {
            draft.id = '3841ee7e-eb56-4faa-afcb-bb43c7d026b2';
            const edgeSettingsById = getEdgeSettingsById(draft.edges);
            edgeSettingsById.groups[factoryPresetEdges.layer1].grouped = false;
            edgeSettingsById.groups[factoryPresetEdges.layer2].grouped = false;
            edgeSettingsById.groups[factoryPresetEdges.layer3].grouped = false;
          }),
        }),
      ),
      call(requestForSaga, {
        url: '/tables/fhrp/group-state',
        method: 'POST',
        data: {
          columns: ['hostname', 'virtualIp', 'intName', 'priority', 'state'],
          filters: siteFilter,
          reports: '/technology/fhrp/group-state',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/interfaces/errors/disabled',
        method: 'POST',
        data: {
          columns: ['hostname', 'intName', 'errorReason'],
          filters: siteFilter,
          reports: '/technology/interfaces/errdisabled',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/addressing/managed-devs',
        method: 'POST',
        data: {
          columns: ['hostname', 'intName', 'ip', 'net', 'type', 'vrf'],
          filters: siteFilter,
          pagination: { limit: 100, start: 0 },
          reports: '/technology/addressing/managed-ip/ipv4',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/inventory/pn',
        method: 'POST',
        data: {
          columns: ['dscr', 'deviceId', 'hostname', 'name', 'pid', 'sn', 'vid'],
          filters: {
            or: [
              { and: [{ pid: ['neq', null] }, { pid: ['neq', ''] }] },
              { and: [{ sn: ['neq', null] }, { sn: ['neq', ''] }] },
            ],
            ...siteFilter,
          },
          reports: '/inventory/part-numbers',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/inventory/sites',
        method: 'POST',
        data: {
          columns: [
            'siteName',
            'devicesCount',
            'usersCount',
            'switchesCount',
            'routersCount',
            'networksCount',
            'vlanCount',
            'rDomains',
            'stpDomains',
          ],
          filters: siteFilter,
          reports: '/inventory/sites',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/security/enabled-telnet',
        method: 'POST',
        data: {
          columns: ['hostname', 'loginIpv4', 'loginIpv6'],
          filters: siteFilter,
          reports: '/technology/management/telnet-access',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/inventory/devices',
        method: 'POST',
        data: {
          columns: ['hostname', 'uptime', 'loginIpv4', 'loginIpv6', 'reload'],
          filters: {
            or: [
              { reload: ['color', 'eq', '20'] },
              { reload: ['color', 'eq', '30'] },
            ],
            ...siteFilter,
          },
          reports: '/inventory/devices',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/inventory/os-version-consistency/platforms',
        method: 'POST',
        data: {
          bindVariables: { siteName },
          columns: ['vendor', 'family', 'platform', 'version', 'count'],
          reports: '/inventory/os-versions/platforms',
          snapshot,
        },
      }),
      call(requestForSaga, {
        url: '/tables/inventory/interfaces',
        method: 'POST',
        data: {
          columns: ['hostname', 'intName', 'speed', 'duplex', 'dscr'],
          filters: {
            duplex: ['eq', 'half'],
            l1: ['eq', 'up'],
            l2: ['eq', 'up'],
            ...siteFilter,
          },
          snapshot,
          reports: '/inventory/interfaces',
        },
      }),
      call(requestForSaga, {
        url: '/tables/vlan/device-summary',
        method: 'POST',
        data: {
          filters: siteFilter,
          snapshot,
          columns: [
            'hostname',
            'sn',
            'siteName',
            'totalVlanCount',
            'activeVlanCount',
          ],
          reports: '/technology/vlans/device-summary',
        },
      }),
    ]);

    const siteRecord = siteInfo.data[0];
    // Get raw value when colorized column is defined
    for (const key of Object.keys(siteRecord)) {
      siteRecord[key] = getValue(siteRecord[key]);
    }
    const {
      rDomains,
      stpDomains,
      devicesCount,
      usersCount,
      switchesCount,
      routersCount,
      networksCount,
      vlanCount,
    } = siteInfo.data[0];

    const data = [
      {
        siteName,
        siteUsersCount: usersCount,
        siteDevsCount: devicesCount,
        siteSwitchesCount: switchesCount,
        siteRoutersCount: routersCount,
        siteRoutingDomainsCount: rDomains.length,
        siteStpDomainsCount: stpDomains.length,
        siteNetworksCount: networksCount,
        siteVlansCount: vlanCount,
        chartData: graph,
        type: 'global',
        cdpAll: cdpAll.data,
        cdpUnidirectional: cdpUnidirectional.data,
        unmanagedNeighbors: cdpUnmanaged.data,
        configRegister: configRegister.data,
        connectivityMatrix: connectivityMatrix.data,
        devices: devices.data,
        duplexInterfaces: duplexInterfaces.data,
        endOfSupport: endOfSupport.data,
        ethChannels: ethChannels.data,
        hsrpStatus: hsrpStatus.data,
        intErrDisabled: intErrDisabled.data,
        managedIp: managedIp.data,
        osVersionsPlatforms: osVersionsPlatforms.data,
        partNumbers: partNumbers.data,
        telnetDevices: telnetDevices.data,
        unexpectedReloads: unexpectedReloads.data,
        vlans: vlans.data,
      },
    ];

    const filteredRDomains = (rDomains || []).filter((rd) => rd > 0);
    const graphDomains = yield all(
      filteredRDomains.map((rd) => {
        return call(
          requestForSaga,
          createGraphApiRequest({
            attributeFilters,
            parameters: {
              groupBy: 'routingDomain',
              paths: [rd],
              type: 'topology',
            },
            snapshotId: snapshot,
            preset: produce(FactoryPreset, (draft) => {
              draft.id = '525c3fa8-5b17-4dc0-8978-9b8d6c529442';
              const edgeSettingsById = getEdgeSettingsById(draft.edges);
              edgeSettingsById.groups[factoryPresetEdges.layer1].visible =
                false;
              edgeSettingsById.groups[factoryPresetEdges.layer2].visible =
                false;
              edgeSettingsById.groups[factoryPresetEdges.layer3].grouped =
                false;
            }),
          }),
        );
      }),
    );
    const dataWithGraphDomains = data.concat(
      filteredRDomains.map((rdID, index) => {
        return {
          id: rdID,
          type: 'routing',
          chartData: graphDomains[index],
        };
      }),
    );

    const graphStpDomainData = yield all(
      stpDomains.map((stpDomainId) =>
        call(
          requestForSaga,
          createGraphApiRequest({
            attributeFilters,
            parameters: {
              groupBy: 'stpDomain',
              paths: [stpDomainId],
              type: 'topology',
            },
            snapshotId: snapshot,
            preset: produce(FactoryPreset, (draft) => {
              draft.id = '8f812c42-69d3-4a08-bae6-8e0f7522e7d5';
              const edgeSettingsById = getEdgeSettingsById(draft.edges);
              for (const edgeSettings of Object.values(
                edgeSettingsById.topology,
              )) {
                if (edgeSettings.name !== 'stp') {
                  edgeSettings.visible = false;
                }
              }
              edgeSettingsById.groups[factoryPresetEdges.layer1].visible =
                false;
              edgeSettingsById.groups[factoryPresetEdges.layer2].grouped =
                false;
              edgeSettingsById.groups[factoryPresetEdges.layer3].visible =
                false;
            }),
          }),
        ),
      ),
    );

    const dataWithStp = dataWithGraphDomains.concat(
      stpDomains.map((stpDomainID, index) => {
        return {
          id: stpDomainID,
          type: 'stp',
          chartData: graphStpDomainData[index],
          instances: [],
          bridges: [],
        };
      }),
    );

    resolve({
      data: dataWithStp.map((item) => mapTables(item, timezone, dateFormat)),
      siteName,
      endTime: date.formatUtcTimestampInTimeZone(
        get(analysis, ['data', 0, 'tsEnd']),
        timezone,
        dateTimeFormat,
      ),
    });
    yield put({ type: `${FETCH_SITE_REPORT_DATA}_SUCCESS` });
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    reject();
    throw e;
  }
}

// Individual exports for testing
export function* defaultSaga() {
  yield takeEvery(FETCH_SITE_REPORT_DATA, fetchDownloadSiteReportSaga);
}

// All sagas to be loaded
export default defaultSaga;
